import { connect } from '@lib/connect';
import { t } from '@lib/translation';
import { cleanProps } from '@lib/utils';
import { OverrideProps } from '@material-ui/core/OverridableComponent';
import Typography, { TypographyProps } from '@material-ui/core/Typography';
import { createMemo } from '@packages/react';
import { unset } from 'lodash';
import React, { Children } from 'react';
import styled from 'styled-components';

export interface TransTypeMap<P = Record<string, any>, D extends React.ElementType = 'span'> {
    props: P & {
        component?: any;
        key?: string;
        prefix?: string;
        before?: string;
        after?: string;
        props?: any;
        children: any;
        withTitle?: boolean;
        uppercase?: boolean;
        lowercase?: boolean;
        capitalize?: boolean;
        transform?: string;
    };
    defaultComponent: D;
    classKey: '';
}

type TransProps<D extends React.ElementType = TransTypeMap['defaultComponent'], P = Record<string, any>> =
    OverrideProps<TransTypeMap<P, D>, D>;

type I18nProps = TypographyProps & TransProps;

/**
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @class: Trans
 * @date:  09.09.2018
 * @time:  19:07
 * @scope:
 */
class TransComponent<C extends React.ElementType> extends React.PureComponent<TransProps<C, { component?: C }>> {
    static displayName = 'Trans';

    static defaultProps = {
        component: React.Fragment,
    };

    /**
     * Render Trans Component
     */
    render() {
        const {
            key,
            prefix,
            props,
            before,
            after,
            children,
            uppercase,
            withTitle,
            lowercase,
            capitalize,
            transform,
            component,
            ...rest
        } = this.props;
        const Component: any = component;

        cleanProps(rest, 'localePrefix', 'locale', 'dispatch');

        let children$: any = children;

        if (key) {
            children$ = key;
        }

        if (children$ && !Array.isArray(children$)) {
            children$ = [children$];
        }

        /**
         * Translate all child
         */
        children$ = Children.map(children$, (child) => {
            // TODO: implement deep transformation
            let newChild: any = child;

            if (typeof child === 'object') {
                newChild = { ...child } as any;
                newChild.props = { ...newChild.props };

                if (newChild.props.trans && !newChild.props.trans.ignore) {
                    newChild.props.children = t(newChild.props.children, {
                        ...newChild.props.trans,
                        props: newChild.props.trans || props,
                    });
                }

                unset(newChild.props, 'trans');

                return newChild;
            }

            return t(newChild, {
                props,
                uppercase,
                lowercase,
                capitalize,
                after,
                before,
                prefix,
                transform,
            });
        });

        if (withTitle && typeof children === 'string') {
            // @ts-ignore
            rest.title = children$;
        }

        return <Component {...rest}>{children$}</Component>;
    }
}

export const Trans = connect({
    localePrefix: 'Internalization.configuration.prefix',
    locale: 'Internalization.language',
})(TransComponent);

export const I18n = createMemo<I18nProps>(
    ({ component = Typography, children, ref, ...rest }) => (
        <Trans ref={ref as any} component={component} {...rest}>
            {children}
        </Trans>
    ),
    {
        displayName: 'I18n',
    },
);

export const I18nStyledH1 = styled(I18n)`
    && {
        font-size: 3.5rem;
        @media screen and (min-width: 768px) and (max-width: 1024px) {
            font-size: 3rem;
        }

        @media screen and (max-width: 767px) {
            font-size: 2.5rem;
        }

        @media screen and (max-width: 640px) {
            font-size: 2rem;
        }
    }
`;
